import { OPERATION_MODE } from "@/common/constants";
import { INVENTORY_MODES } from "@/common/fields/inventory-modes";
import { OperationsModes } from "@/common/fields/operations-modes";
import { verifyStrictPermission } from "@/common/lib/permissions";

// billing-fase5-salida-planes: features y topes de los planes, con los
// nombres del requirement. `permission` es lo que el plan otorga (el
// backend da `feature:Activate<X>` por feature); `flagKey` es el booleano
// manual que el tenant prende o apaga (null si la feature no tiene).
// `comingSoon`: el plan ya la vende pero todavía no está construida; Mis
// funciones la muestra sin interruptor.
export const PLAN_FEATURE_GROUPS = [
    { id: "catalog", label: "Catálogo" },
    { id: "sales", label: "Ventas" },
    { id: "operation", label: "Operación" }
];

export const PLAN_FEATURES = [
    {
        feature: "catalogHideWatermark",
        icon: "✨",
        upgradePermission: "upgrade:FeatureCatalogHideWatermark",
        label: "Quitar la marca de agua",
        description:
            "Oculta el aviso “Crea tu catálogo gratis” al final de tu catálogo.",
        permission: "tenant:UpdateWatermarkSettings",
        flagKey: null,
        group: "catalog"
    },
    {
        feature: "displayModeCatalog",
        icon: "🗂️",
        label: "Vista de catálogo",
        description: "Tus enlaces arriba y tu vitrina de productos debajo.",
        permission: "feature:ActivateDisplayModeCatalog",
        flagKey: null,
        group: "catalog"
    },
    {
        feature: "products",
        icon: "📦",
        upgradePermission: "upgrade:FeatureProducts",
        label: "Productos",
        description: "Muestra y vende productos en tu catálogo.",
        permission: "feature:ActivateProducts",
        flagKey: "productsEnabled",
        group: "catalog",
        // pedidos-whatsapp: backend no deja apagarla con productos publicados.
        enabledHint: "Para apagarla, primero despublica tus productos."
    },
    {
        feature: "productVariants",
        icon: "🎨",
        upgradePermission: "upgrade:FeatureProductVariants",
        label: "Variantes de producto",
        description:
            "Tallas, colores y referencias personalizadas en tus productos.",
        permission: "feature:ActivateProductVariants",
        flagKey: null,
        group: "catalog"
    },
    {
        feature: "displayModeSegments",
        icon: "🧱",
        upgradePermission: "upgrade:FeatureDisplayModeSegments",
        label: "Vista por bloques",
        description: "Arma tu página con el editor de bloques.",
        permission: "feature:ActivateDisplayModeSegments",
        flagKey: null,
        group: "catalog"
    },
    {
        feature: "displayModeMix",
        icon: "🧩",
        upgradePermission: "upgrade:FeatureDisplayModeMix",
        label: "Vista mixta",
        description: "Bloques y productos en la misma página.",
        permission: "feature:ActivateDisplayModeMix",
        flagKey: null,
        group: "catalog"
    },
    {
        feature: "lines",
        icon: "🏷️",
        upgradePermission: "upgrade:FeatureLines",
        label: "Categorías",
        description: "Organiza tus productos por categorías.",
        permission: "feature:ActivateLines",
        flagKey: "linesEnabled",
        group: "catalog"
    },
    {
        feature: "brands",
        icon: "🔖",
        upgradePermission: "upgrade:FeatureBrands",
        label: "Marcas",
        description: "Agrupa tus productos por marca.",
        permission: "feature:ActivateBrands",
        flagKey: "brandsEnabled",
        group: "catalog"
    },
    {
        feature: "collections",
        icon: "⭐",
        upgradePermission: "upgrade:FeatureCollections",
        label: "Colecciones",
        description: "Arma colecciones de productos para destacar.",
        permission: "feature:ActivateCollections",
        flagKey: "collectionsEnabled",
        group: "catalog"
    },
    {
        feature: "blogs",
        icon: "📝",
        upgradePermission: "upgrade:FeatureBlogs",
        label: "Páginas personalizadas",
        description: "Páginas aparte: contacto, quiénes somos, blog.",
        permission: "feature:ActivateBlogs",
        flagKey: "blogsEnabled",
        group: "catalog"
    },
    {
        // pedidos-whatsapp: en todos los planes. `requiresProducts` solo
        // desactiva el interruptor en pantalla; el valor guardado no cambia.
        feature: "whatsappOrders",
        icon: "💬",
        label: "Pedidos por WhatsApp",
        description:
            "Tus clientes arman un carrito y te mandan el pedido por WhatsApp.",
        permission: "feature:ActivateWhatsappOrders",
        flagKey: "whatsappOrdersEnabled",
        group: "sales",
        requiresProducts: true
    },
    {
        feature: "internalOrders",
        icon: "🧾",
        upgradePermission: "upgrade:FeatureInternalOrders",
        label: "Ventas internas",
        description: "Registra ventas y clientes desde el portal.",
        permission: "feature:ActivateInternalOrders",
        flagKey: null,
        group: "sales"
    },
    {
        feature: "quotes",
        icon: "📄",
        upgradePermission: "upgrade:FeatureQuotes",
        label: "Cotizaciones",
        description: "Envía cotizaciones en PDF.",
        permission: "feature:ActivateQuotes",
        flagKey: null,
        group: "sales"
    },
    {
        feature: "onlineOrders",
        icon: "🛍️",
        upgradePermission: "upgrade:FeatureOnlineOrders",
        label: "Pedidos desde el catálogo",
        description: "Tus clientes te hacen pedidos desde tu link.",
        permission: "feature:ActivateOnlineOrders",
        flagKey: "onlineOrdersEnabled",
        group: "sales",
        requiresProducts: true,
        comingSoon: true
    },
    {
        feature: "onlinePaymentMethods",
        icon: "💳",
        upgradePermission: "upgrade:FeatureOnlinePaymentMethods",
        label: "Pagos en el catálogo",
        description: "Activa medios de pago para los pedidos de tu link.",
        permission: "feature:ActivateOnlinePaymentMethods",
        flagKey: "onlinePaymentMethodsEnabled",
        group: "sales",
        requiresProducts: true,
        comingSoon: true
    },
    {
        feature: "onlineOrdersNotifications",
        icon: "🔔",
        upgradePermission: "upgrade:FeatureOnlineOrdersNotifications",
        label: "Avisos de pedidos",
        description: "Recibe un aviso cada vez que llega un pedido.",
        permission: "feature:ActivateOnlineOrdersNotifications",
        flagKey: "onlineOrdersNotificationsEnabled",
        group: "sales",
        requiresProducts: true,
        comingSoon: true
    },
    {
        // sucursales-inventario-multitenant: no es un booleano — el flag es
        // `inventoryMode` (activa fuera de `none`, ver `isFeatureFlagOn`), de
        // solo lectura acá; se cambia el modo en `settingsUrl`.
        feature: "inventory",
        icon: "📊",
        upgradePermission: "upgrade:FeatureInventory",
        label: "Inventario",
        description:
            "Controla el stock de cada producto, con descuento automático en cada venta.",
        permission: "feature:ActivateInventory",
        flagKey: "inventoryMode",
        settingsUrl: "/app/settings/inventory",
        group: "operation"
    },
    {
        feature: "inventoryByMerchant",
        icon: "🧮",
        upgradePermission: "upgrade:FeatureInventoryByMerchant",
        label: "Inventario por sucursal",
        description: "Cada sucursal con su propio inventario y sus precios.",
        permission: "feature:ActivateInventoryByMerchant",
        flagKey: null,
        settingsUrl: "/app/settings/inventory",
        group: "operation"
    },
    {
        feature: "stores",
        icon: "🏬",
        upgradePermission: "upgrade:FeatureStores",
        label: "Sucursales",
        description: "Maneja varias sedes de tu negocio.",
        permission: "feature:ActivateStores",
        flagKey: "storesEnabled",
        group: "operation"
    },
    {
        // api-publica-docs-y-permisos: se habilita por el entitlement
        // `apiKeys` del plan (Pro 2, Premium 5); el rol lo refleja con
        // `apiKey:*` y el candado con `upgrade:FeatureApiKeys`.
        feature: "apiKeys",
        icon: "🔑",
        upgradePermission: "upgrade:FeatureApiKeys",
        label: "API para integraciones",
        description: "Conecta tu ERP, CRM u otros sistemas con api keys.",
        permission: "apiKey:List",
        flagKey: null,
        group: "operation"
    }
];

// `products`/`blogs`/`apiKeys` no vienen en `plan.features`: el backend los
// habilita por su tope.
export const ENTITLEMENT_FEATURES = ["products", "blogs", "apiKeys"];

export const PLAN_RESOURCES = [
    { resourceCode: "products", label: "productos" },
    { resourceCode: "segments", label: "bloques" },
    { resourceCode: "customLinks", label: "enlaces" },
    { resourceCode: "blogs", label: "páginas personalizadas" },
    { resourceCode: "apiKeys", label: "api keys" },
    { resourceCode: "stores", label: "sucursales" }
];

// Grupo de las homecards (`home-cards-items.js`) → feature del plan.
export const HOME_CARD_GROUP_FEATURES = {
    products: "products",
    orders: "internalOrders",
    inventories: "inventory"
};

const FEATURE_ACTIVATION_PREFIX = "feature:Activate";

// portal-sesion-y-candados-optimizacion: nombre visible del plan a partir
// del `planCode` de la sesión, sin pedir el catálogo de planes.
export const PLAN_NAMES = {
    free: "Free",
    starter: "Starter",
    pro: "Pro",
    premium: "Premium"
};

export const planNameFromCode = (planCode) => PLAN_NAMES[planCode] || planCode;

export const findPlanFeature = (feature) =>
    PLAN_FEATURES.find((item) => item.feature === feature) || null;

export const findPlanResource = (resourceCode) =>
    PLAN_RESOURCES.find((item) => item.resourceCode === resourceCode) || null;

// Orden de los planes, del más barato al más completo.
export const PLAN_ORDER = ["free", "starter", "pro", "premium"];

// Plan inmediatamente superior (mismo criterio que los `upgrade:*` de cada
// `owner_*`); null si ya está en el último o el code no se conoce.
export const nextPlanCode = (planCode) => {
    const index = PLAN_ORDER.indexOf(planCode);
    if (index < 0 || index === PLAN_ORDER.length - 1) return null;
    return PLAN_ORDER[index + 1];
};

// portal-sesion-y-candados-optimizacion: 3 beneficios por plan pago para la
// tarjeta del home y los modales de planes, sin pedir `platform-plans`.
// Sale de lo que cada plan desbloquea respecto al anterior; Premium solo
// suma inventario por sucursal y sucursales, así que completa con pedidos
// online.
export const PLAN_BENEFITS = {
    starter: ["products", "catalogHideWatermark", "displayModeSegments"],
    pro: ["onlineOrders", "onlinePaymentMethods", "lines"],
    premium: ["inventoryByMerchant", "stores", "onlineOrders"]
};

// Texto propio de un beneficio en un plan, cuando el genérico no dice lo que
// cambia (Free ya trae 3 productos: lo que suma Starter es el cupo).
const PLAN_BENEFIT_OVERRIDES = {
    starter: {
        products: {
            label: "Hasta 24 productos",
            description: "Pasa de 3 a 24 productos en tu catálogo."
        }
    }
};

// Beneficios listos para mostrar: [{ feature, label, description, icon }].
export const planBenefits = (planCode) =>
    (PLAN_BENEFITS[planCode] || [])
        .map(findPlanFeature)
        .filter(Boolean)
        .map(({ description, feature, icon, label }) => ({
            feature,
            label,
            description,
            icon,
            ...((PLAN_BENEFIT_OVERRIDES[planCode] || {})[feature] || {})
        }));

// Tope de un recurso en un plan de `GET /bo/platform-plans`; null es sin
// límite y sin entitlement es 0.
export const planLimitOf = (plan, resourceCode) => {
    const entitlement = (plan.entitlements || []).find(
        (item) => item.resourceCode === resourceCode
    );
    return entitlement ? entitlement.quantity : 0;
};

export const planIncludesFeature = (plan, feature) => {
    if (ENTITLEMENT_FEATURES.includes(feature)) {
        const limit = planLimitOf(plan, feature);
        return limit === null || limit > 0;
    }
    return (plan.features || []).includes(feature);
};

// Lo que se deja de tener al pasar de `fromPlan` a `toPlan`: funciones que
// `toPlan` no incluye y recursos (`usage` de `GET .../mine`) por encima de
// su tope.
export const listPlanLosses = ({ fromPlan, toPlan, usage = [] }) => ({
    features: PLAN_FEATURES.filter(
        ({ feature }) =>
            planIncludesFeature(fromPlan, feature) &&
            !planIncludesFeature(toPlan, feature)
    ),
    resources: usage
        .map((item) => {
            const resource = findPlanResource(item.resourceCode);
            return {
                resourceCode: item.resourceCode,
                label: resource ? resource.label : item.resourceCode,
                used: item.used,
                limit: planLimitOf(toPlan, item.resourceCode)
            };
        })
        .filter((item) => item.limit !== null && item.used > item.limit)
});

// Los planes solo existen en multi-tenant (tucatalogo); las franquicias y
// los ecommerce de un solo tenant nunca ven candados. Dentro de
// multi-tenant, un tenant del modelo de planes siempre tiene algún
// `feature:Activate*` (todo `owner_*` tiene al menos el de la vista de
// catálogo); los roles viejos y `support_*` no.
export const isPlanManaged = (permissions = []) =>
    OPERATION_MODE === OperationsModes.multiTenant &&
    permissions.some((permission) =>
        permission.startsWith(FEATURE_ACTIVATION_PREFIX)
    );

export const hasPlanFeature = (permissions = [], feature) => {
    const item = findPlanFeature(feature);
    return Boolean(item) && permissions.includes(item.permission);
};

// Candado de una feature que el rol no tiene: solo con su `upgrade:Feature<X>`
// (comparación estricta).
export const canShowUpgrade = (permissions = [], feature) => {
    const item = findPlanFeature(feature);
    return (
        Boolean(item && item.upgradePermission) &&
        verifyStrictPermission(permissions, item.upgradePermission)
    );
};

// Si el flag de la feature está prendido: los manuales son booleanos;
// `inventoryMode` cuenta como prendido en cualquier modo distinto de `none`.
export const isFeatureFlagOn = (featureFlags = {}, flagKey) => {
    const value = featureFlags[flagKey];
    if (flagKey === "inventoryMode") {
        return Boolean(value) && value !== INVENTORY_MODES.none;
    }
    return Boolean(value);
};

// El plan incluye la feature pero el tenant la tiene apagada (solo las
// manuales tienen booleano).
export const isPlanFeatureOff = (featureFlags = {}, feature) => {
    const item = findPlanFeature(feature);
    return (
        Boolean(item && item.flagKey) &&
        !isFeatureFlagOn(featureFlags, item.flagKey)
    );
};

// Vistas del catálogo por plan (mismo criterio que valida el backend al
// guardar): `feed` siempre; el resto con su feature, y las que muestran
// productos además con `productsEnabled`.
const DISPLAY_MODE_FEATURES = {
    catalog: { feature: "displayModeCatalog", requiresProducts: true },
    segments: { feature: "displayModeSegments", requiresProducts: false },
    mix: { feature: "displayModeMix", requiresProducts: true }
};

export const DISPLAY_MODE_STATES = {
    available: "available",
    locked: "locked",
    needsProducts: "needsProducts"
};

export const displayModeFeature = (displayMode) =>
    DISPLAY_MODE_FEATURES[displayMode]
        ? DISPLAY_MODE_FEATURES[displayMode].feature
        : null;

// billing-productos-displaymode-permisos: `catalog`/`mix` pueden quedar
// bloqueados por dos motivos distintos — la feature propia del modo
// (`displayModeCatalog`/`displayModeMix`, plan no la incluye) o el tope de
// `products` (plan con tope 0, ej. Free/`owner_free`: nunca puede elegir
// estos modos sin importar la feature). El tope manda: un plan sin
// `products` no desbloquea catalog/mix aunque sí tenga la feature propia
// del modo. `displayModeLockFeature` decide cuál de los dos es la causa,
// para pedirle al plan correcto en el modal "Pásate a…" — nunca
// `unlockPlanForFeature(displayModeFeature)` a ciegas, que en el caso del
// tope devolvería un plan sin sentido (ej. el propio Free, que ya incluye
// la feature pero no el tope).
export const displayModeLockFeature = (displayMode, permissions = []) => {
    const rule = DISPLAY_MODE_FEATURES[displayMode];
    if (!rule) return null;
    if (rule.requiresProducts && !hasPlanFeature(permissions, "products")) {
        return "products";
    }
    return rule.feature;
};

export const displayModeState = (
    displayMode,
    permissions = [],
    featureFlags = {}
) => {
    const rule = DISPLAY_MODE_FEATURES[displayMode];
    if (!rule || !isPlanManaged(permissions)) {
        return DISPLAY_MODE_STATES.available;
    }
    if (
        (rule.requiresProducts && !hasPlanFeature(permissions, "products")) ||
        !hasPlanFeature(permissions, rule.feature)
    ) {
        return DISPLAY_MODE_STATES.locked;
    }
    if (rule.requiresProducts && !featureFlags.productsEnabled) {
        return DISPLAY_MODE_STATES.needsProducts;
    }
    return DISPLAY_MODE_STATES.available;
};

// La feature está prendida para el tenant: el plan la permite y, si es
// manual, su booleano está en true.
export const isPlanFeatureOn = (
    permissions = [],
    featureFlags = {},
    feature
) => {
    const item = findPlanFeature(feature);
    if (!item || !permissions.includes(item.permission)) return false;
    return item.flagKey ? isFeatureFlagOn(featureFlags, item.flagKey) : true;
};
